<template>
  <full-screen-container>
    <div v-if="isFetchingData" id="preloader">
      <i-spinner size="xl"></i-spinner>
    </div>
    <div
      v-else
      :class="{
        'left-panel-open': leftPanelOpen,
        'right-panel-open': rightPanelOpen,
      }"
      class="document-results-panel"
      data-testid="document-results-panel"
    >
      <document-header
        v-if="shouldShowHeader"
        :document="document"
        :customer="customer"
        @back="close"
        @document-deleted="onDocumentDeleted"
        @toggle-history="onToggleHistory"
        @open-similar-documents="onOpenSimilarDocuments"
      ></document-header>
      <div class="document-results-content full-screen-content">
        <document-left-panel
          v-if="shouldShowLeftPanel"
          :document="document"
          @toggle-panel="onPanelsToggle"
        ></document-left-panel>
        <document-view-selector
          v-if="shouldShowViewSelector"
          :document="document"
          class="document-view-selector-container"
        ></document-view-selector>
        <router-view
          :document="document"
          :rotation="rotation"
          :class="{
            'with-right-panel': shouldShowRightPanel && rightPanelOpen,
          }"
          class="document-container"
          @rotate="rotation = (rotation + 90) % 360"
        />
        <document-right-panel
          v-if="shouldShowRightPanel"
          ref="documentRightPanel"
          :customer-id="customerId"
          :document="document"
          :config="rightPanelConfig"
          @toggle-panel="onPanelsToggle"
          @close-overlay="onCloseOverlay"
        ></document-right-panel>
      </div>
      <chat-floating-prompt-box v-if="isChatAgentEnabled" />
    </div>
  </full-screen-container>
</template>

<script>
import DocumentHeader from '@/V2/documents/DocumentHeader';
import DocumentRightPanel from '@/V2/documents/document-view/DocumentRightPanel';
import DocumentLeftPanel from '@/V2/documents/document-view/DocumentLeftPanel';
import DocumentViewSelector from '@/V2/documents/DocumentViewSelector.vue';
import FullScreenContainer from '@/V2/documents/document-api/FullScreenContainer.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { iSpinner, showToast } from '@inscribe/signal';
import ChatFloatingPromptBox from '@/V2/chat/ChatFloatingPromptBox.vue';

export default {
  components: {
    DocumentHeader,
    DocumentLeftPanel,
    DocumentRightPanel,
    FullScreenContainer,
    DocumentViewSelector,
    iSpinner,
    ChatFloatingPromptBox,
  },
  data() {
    return {
      error: false,
      rotation: 0,
      leftPanelOpen: true,
      rightPanelOpen: true,
      documentDeleted: false,
      historyOpen: false,
      rightPanelConfig: null,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedDocumentById',
      'getCachedCustomerById',
      'isChatAgentEnabled',
    ]),
    documentId() {
      return this.$route.params.document_id;
    },
    customerId() {
      return this.$route.params.customer_id;
    },
    document() {
      return this.getCachedDocumentById(this.documentId);
    },
    customer() {
      return this.getCachedCustomerById(this.customerId);
    },
    isFetchingData() {
      return !this.document || !this.customer;
    },
    shouldShowHeader() {
      return (
        this.$route.query?.hideDocHeader?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    shouldShowViewSelector() {
      return [
        'document-signals',
        'document-text',
        'document-highlighter',
        'document-parsed-details-highlighter',
      ].includes(this.$route.name);
    },
    shouldShowLeftPanel() {
      return (
        this.$route.query?.hideDocLeftPanel?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    shouldShowRightPanel() {
      return (
        this.$route.query?.hideDocRightPanel?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    viewerOnlyMode() {
      return this.$route.query?.viewerOnlyMode?.toString() === 'true';
    },
  },
  watch: {
    '$route.params.document_id': {
      immediate: true,
      async handler(documentId) {
        const promises = [
          this.fetchAndCacheDocument({
            customerId: this.customerId,
            documentId,
          }),
          this.fetchAndCacheCustomer(this.customerId),
          this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
            this.customerId,
          ),
        ];
        const [documentPromise] = await Promise.allSettled(promises);
        if (documentPromise.status === 'rejected') {
          this.close();
          showToast({
            title: 'Problem fetching document',
            text: "We couldn't find that document. You might not have permission to view it, or it might have been deleted.",
            isError: true,
          });
        }
      },
    },
  },
  mounted() {
    if (Object.keys(this.$store.state.config.rulesSchema).length === 0) {
      this.$store.dispatch('getConfigRulesSchema');
    }
    this.resetScoreDistribution();
  },
  methods: {
    ...mapActions([
      'fetchAndCacheDocument',
      'fetchAndCacheCustomer',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
    ]),
    ...mapMutations(['resetScoreDistribution']),
    onDocumentDeleted() {
      this.documentDeleted = true;
    },
    onToggleHistory() {
      this.historyOpen = !this.historyOpen;
      if (this.historyOpen) {
        this.rightPanelConfig = { panel: 'history' };
        this.rightPanelOpen = true;
      } else {
        this.rightPanelConfig = null;
      }
    },
    close() {
      const { return_to, page } = this.$route.query;
      if (return_to) {
        this.$router.push(decodeURIComponent(return_to));
      } else if (this.customerId) {
        this.$router.push({
          name: 'customer-overview',
          params: {
            customer_id: this.customerId,
          },
          query: { page },
        });
      } else {
        this.$router.push('/');
      }
    },
    onCloseOverlay(type) {
      this.historyOpen = false;
    },
    onPanelsToggle(side) {
      if (side === 'left') {
        this.leftPanelOpen = !this.leftPanelOpen;
      } else if (side === 'right') {
        this.rightPanelOpen = !this.rightPanelOpen;

        // move intercom button if necessary
        const intercomLauncher =
          document.getElementsByClassName('intercom-launcher');
        if (intercomLauncher.length) {
          intercomLauncher[0].style.bottom = this.rightPanelOpen
            ? '20px'
            : '70px';
        }
      }
    },
    onOpenSimilarDocuments() {
      // Ensure right panel is open
      if (!this.rightPanelOpen) {
        this.rightPanelOpen = true;
      }
      // Wait for panel to be rendered, then open similar documents
      this.$nextTick(() => {
        if (this.$refs.documentRightPanel) {
          this.$refs.documentRightPanel.openSimilarDocuments();
        }
      });
    },
  },
};
</script>

<style lang="scss">
.document-results-panel {
  $right-panel-width: 460px;
  $left-panel-width: 260px;
  $transition-time: 150ms;

  z-index: 1001;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: $light-neutral-30;

  &.left-panel-open {
    .left-panel {
      min-width: $left-panel-width;
      width: $left-panel-width;

      .scroll-container {
        opacity: 1;
      }
    }

    .document-view-selector-container {
      left: $left-panel-width + 16px;
    }
  }

  &.right-panel-open {
    .right-panel {
      min-width: $right-panel-width;
      width: $right-panel-width;
    }
  }

  .document-container.with-right-panel {
    padding-right: $right-panel-width !important;
    .pdf-controls {
      right: 480px; // 460 right panel + padding
      width: 44px;
    }
  }

  .document-view-selector-container {
    position: absolute;
    top: 80px;
    z-index: 100;
    left: 16px;
  }

  .pdf-controls {
    position: absolute;
    z-index: 100;
    right: 24px;
    top: 80px;
  }

  .left-panel {
    min-width: 0;
    max-width: 0;
    transition:
      width,
      min-width $transition-time ease-in-out;

    .scroll-container {
      opacity: 0;
      transition: opacity 150ms ease-in-out;
    }
  }

  .right-panel {
    position: fixed;
    top: 65px;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: $light-neutral-default;
    min-width: 0;
    max-width: 0;
    border-left: 1px solid $light-neutral-60;
    transition:
      width,
      min-width $transition-time ease-in-out;
  }

  .section-title {
    color: $light-neutral-120;
    text-transform: capitalize;
    font-size: $default-font-size;
    font-weight: map.get($text-weights, 'semibold');
  }

  .modal-body {
    background: $color-almost-black-20 !important;
    display: flex;
    max-height: 100%;
    overflow: hidden;
    padding: 0 !important;
  }

  .i-accordion {
    .i-accordion-item {
      border-bottom: 1px solid $light-neutral-60;
      margin-bottom: 0 !important;
      border-radius: 0 !important;

      &.half-margin {
        .i-accordion-item__article {
          margin-left: 12px !important;
          margin-right: 12px !important;
        }
      }

      .container {
        margin-left: 10px !important;
      }

      .i-accordion-item__article {
        margin-bottom: 0 !important;
      }

      .accordion-content {
        padding-bottom: 24px;
        padding-top: 0;
      }
    }
    .i-accordion-item__title {
      min-height: 64px;

      .title-container {
        height: 16px !important;
      }
      span {
        color: $light-neutral-120;
        font-size: 14px !important;
        font-weight: 600 !important;
      }
    }
  }

  &.left-panel-open {
    .chat-floating-prompt-box {
      margin-left: 130px; // half left panel width
    }
  }

  &.right-panel-open {
    .chat-floating-prompt-box {
      margin-left: -230px; // half right panel width
    }
  }

  &.left-panel-open.right-panel-open {
    .chat-floating-prompt-box {
      margin-left: -100px; // difference panels
    }
  }

  .chat-floating-prompt-box {
    left: 50%;
    margin-left: 0;
  }
}
</style>
